import { Box, Typography, Stack, Grid, Button, Chip } from "@mui/material";
import {
  metricasCliente,
  pipelineExpansionCliente,
  valorPotencialCliente,
  oportunidadesAbiertas,
  senalesOportunidad,
} from "./expansionMetrics";
import {
  labelEtapaExpansion,
  labelTipoOportunidad,
  labelOrigenOportunidad,
  esOrigenOportunidadValido,
} from "./expansionConstants";

function fmtMonto(moneda, n) {
  if (!(Number(n) > 0)) return "Pendiente de estimación";
  return `${moneda} ${Number(n).toLocaleString()}`;
}

export default function ClienteComercialResumen({
  store,
  leadId,
  onEditOportunidad,
  onNuevaOportunidad,
  onSeguimientoOportunidad,
  compact = false,
}) {
  const moneda = store.config.moneda || "";
  const ventas = store.ventas ?? [];
  const opps = (store.oportunidadesExpansion || []).filter((o) => o.leadId === leadId);
  const abiertas = oportunidadesAbiertas(opps);
  const m = metricasCliente(ventas, leadId);
  const pipeExp = pipelineExpansionCliente(opps, leadId);
  const potencial = valorPotencialCliente(ventas, opps, leadId);
  const dias = store.config.diasEntreSeguimiento || 7;

  if (m.compras === 0 && opps.length === 0) return null;

  return (
    <Box sx={{ mt: compact ? 1 : 2, p: 2, bgcolor: "grey.50", borderRadius: 2, border: 1, borderColor: "divider" }}>
      <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
        Resumen del cliente
      </Typography>
      <Grid container spacing={1.5} mb={2}>
        {[
          ["Revenue histórico", `${moneda} ${m.revenue.toLocaleString()}`, "success.main"],
          ["Compras realizadas", String(m.compras), "text.primary"],
          ["Ticket promedio", `${moneda} ${m.ticketPromedio.toLocaleString()}`, "text.primary"],
          ["Pipeline expansión", `${moneda} ${pipeExp.toLocaleString()}`, "warning.dark"],
          ["Valor potencial", `${moneda} ${potencial.toLocaleString()}`, "primary.main"],
        ].map(([label, value, color]) => (
          <Grid item xs={6} sm={4} key={label}>
            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
            <Typography variant="body2" fontWeight={700} color={color}>{value}</Typography>
          </Grid>
        ))}
      </Grid>
      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
        LTV realizado = revenue histórico. Valor potencial incluye oportunidades abiertas estimadas.
      </Typography>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle2" fontWeight={700}>Oportunidades actuales</Typography>
        {m.compras > 0 && onNuevaOportunidad && (
          <Button size="small" onClick={() => onNuevaOportunidad(leadId)}>Nueva oportunidad</Button>
        )}
      </Stack>
      {abiertas.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Sin oportunidades abiertas.</Typography>
      ) : (
        <Stack spacing={1}>
          {abiertas.map((o) => {
            const senales = senalesOportunidad(o, { diasEstancamiento: dias });
            const vendedor = store.config.vendedores.find((v) => v.id === o.vendedorId)?.nombre || "—";
            return (
              <Box
                key={o.id}
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: "background.paper",
                  border: 1,
                  borderColor: senales.length ? "warning.light" : "grey.200",
                }}
              >
                <Typography variant="body2" fontWeight={700}>{o.nombre}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {labelTipoOportunidad(o.tipo)}
                  {o.origen && esOrigenOportunidadValido(o.origen) && (
                    <> · Origen: {labelOrigenOportunidad(o.origen)}</>
                  )}
                </Typography>
                {o.detalleOrigen?.trim() && (
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {o.detalleOrigen.trim()}
                  </Typography>
                )}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={0.5}>
                  <Chip size="small" label={labelEtapaExpansion(o.etapa)} variant="outlined" />
                  <Typography variant="caption">{fmtMonto(moneda, o.montoEstimado)}</Typography>
                </Stack>
                {o.proximaAccion && (
                  <Typography variant="caption" display="block" mt={0.5}>
                    Próxima acción: {o.proximaAccion}
                  </Typography>
                )}
                {o.proximoSeguimiento && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Seguimiento: {o.proximoSeguimiento.slice(5).replace("-", " ")}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block">Responsable: {vendedor}</Typography>
                {senales.length > 0 && (
                  <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap" useFlexGap>
                    {senales.map((s) => (
                      <Chip key={s} size="small" label={s.replace(/_/g, " ")} sx={{ height: 20, fontSize: 10 }} />
                    ))}
                  </Stack>
                )}
                <Stack direction="row" spacing={1} mt={1}>
                  {onEditOportunidad && (
                    <Button size="small" onClick={() => onEditOportunidad(o)}>Editar</Button>
                  )}
                  {onSeguimientoOportunidad && (
                    <Button size="small" variant="outlined" onClick={() => onSeguimientoOportunidad(o)}>Seguimiento</Button>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
